import type { Router } from 'vue-router'

export default defineNuxtPlugin((nuxtApp) => {
  const maxReload = 10
  // Consecutive chunk errors within this window count toward the reload cap;
  // an error further apart is treated as isolated and resets the counter.
  const loopWindowMs = 30_000
  const chunkErrorReloads = useLocalStorage('chunkErrorReloads', 0)
  const lastChunkErrorAt = useLocalStorage('chunkErrorLastAt', 0)
  const log = useLog('routeChunkErrorHandler')

  // Routes where a full reload would wipe in-progress user input. On these we
  // skip the automatic reload and let the user recover manually (ECOM-2690).
  const formBearingPaths = ['/checkout', '/service/direct-order']
  const isFormBearingRoute = (path: string) =>
    formBearingPaths.some((p) => path.includes(p))

  nuxtApp.hook('app:chunkError', ({ error }) => {
    const router = nuxtApp.$router as Router | undefined
    const currentPath = router?.currentRoute.value.path ?? ''
    if (isFormBearingRoute(currentPath)) {
      log.error(
        `Caught chunk load error on a form route (${currentPath}); skipping auto-reload to preserve user input`,
        { error },
      )
      return
    }

    // Count only *consecutive* failures (within loopWindowMs of the previous
    // one) toward the cap. A chunk error long after the last one is isolated,
    // not a reload loop, so the counter resets — this avoids a permanent
    // lifetime lockout without weakening the loop guard: a persistent failure
    // keeps reloading rapidly, so its errors stay inside the window and the
    // count climbs to the cap. (Replaces the page:finish reset, which cleared
    // the counter on every navigation and defeated the guard — Bugbot review.)
    const now = Date.now()
    const previous = lastChunkErrorAt.get()
    const reloads =
      previous && now - previous < loopWindowMs ? chunkErrorReloads.get() : 0
    lastChunkErrorAt.set(now)

    if (reloads < maxReload) {
      log.error(`Caught chunk load error, reloading shop (count: ${reloads})`, {
        error,
      })
      chunkErrorReloads.set(reloads + 1)
      reloadNuxtApp()
    } else {
      log.error('Caught chunk load error, reload entered a loop', { error })
    }
  })
})
